import type { KeyboardEvent } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { ArrowLongRightIcon, CloseIcon } from '@truvhq/ui';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

type ChatComposerProps = {
  isStreaming: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
};

// trim().min(1) gates empty / whitespace-only input.
const schema = z.object({ message: z.string().trim().min(1) });
type FormValues = z.infer<typeof schema>;

export function ChatComposer({ isStreaming, onSend, onStop }: ChatComposerProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { message: '' },
  });

  const submit = handleSubmit(({ message }) => {
    onSend(message.trim());
    reset({ message: '' });
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Enter sends; Shift+Enter inserts a newline. The Send button is the discoverable path.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  // Floats over the chat (positioned by its parent): a rounded, shadowed white field, no bar behind it.
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Controller
        name="message"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            multiline
            maxRows={6}
            size="small"
            placeholder="Ask the assistant…"
            onKeyDown={handleKeyDown}
            sx={{
              bgcolor: 'common.white',
              borderRadius: 3,
              boxShadow: 3,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isStreaming ? (
                    <IconButton size="small" onClick={onStop} aria-label="Stop generating">
                      <CloseIcon width={16} height={16} />
                    </IconButton>
                  ) : (
                    <IconButton size="small" type="submit" disabled={!isValid} aria-label="Send message">
                      <ArrowLongRightIcon width={16} height={16} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </form>
  );
}
